import Gradient from 'assets/images/gradient.png'
import Keys from 'components/core/Keys'
import { TopBar } from 'components/Layout/TopBar'
import { TopBarContent } from 'components/Layout/TopBarProvider'
import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import { useAuth } from 'hooks/useAuth'
import { ChevronRight, SquareArrowOutUpRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import InsufficientPermissions from './InsufficientPermissions'
import OrgManagement from './OrgManagement'
import OrgHome from './Orgs'
import SettingsPage from './Settings/GeneralSettings'
import SSOSettings from './Settings/SSOSettings'

function valueMapping(value) {
  switch (value) {
    case 'general':
      return 'General Settings'
    case 'users':
      return 'User Management'
    case 'api':
      return 'API Keys'
    case 'add-ons':
      return 'Add-ons'
    case 'orgs':
      return 'Organizations'
    case 'sso':
      return 'Single Sign-On'
    default:
      return 'General Settings'
  }
}

export default function Settings() {
  const { org, permissions } = useAuth()

  const [selectedTab, setSelectedTab] = useState('general')
  const [searchParams] = useSearchParams()
  const tab = searchParams.get('tab')
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(true)
  const [hasPermissions, setHasPermissions] = useState(false)

  useEffect(() => {
    if (!tab)
      return
    switch (tab) {
      case 'orgs':
        setSelectedTab('orgs')
        break
      case 'users':
        setSelectedTab('users')
        break
      case 'keys':
        setSelectedTab('api')
        break
      case 'sso':
        setSelectedTab('sso')
        break
      default:
        setSelectedTab('general')
        break
    }
    searchParams.delete('tab')
  }, [searchParams, tab])

  useEffect(() => {
    // If selectedTarget is not defined yet, we're still loading
    if (!org) {
      setIsLoading(true)
      return
    }

    const canAccess
      = permissions?.includes('owner') || permissions?.includes('admin')
    setHasPermissions(canAccess)
    setIsLoading(false)
  }, [org, permissions])

  const BreadcrumbContent = (
    <div className="flex items-center gap-2.5">
      <img
        src={Gradient}
        alt="default-gradient"
        className="h-[13px] w-[13px] rounded object-cover"
      />
      <p className="font-taurus text-base font-semibold tracking-tighter">
        Organization
      </p>
      <ChevronRight size={15} color="lightgray" strokeWidth={3} />
      <p className="font-taurus text-base font-medium">
        {org?.org_display_name}
      </p>
    </div>
  )

  // If we're still loading, show a loading state
  if (isLoading) {
    return (
      <div>
        <TopBarContent>
          <TopBar.Title>{BreadcrumbContent}</TopBar.Title>
        </TopBarContent>
        <GradientLoadingAnimation variant="arcticAurora" />
      </div>
    )
  }

  // If we have loaded data but user does not have permissions
  if (!hasPermissions) {
    return (
      <div>
        <TopBarContent>
          <TopBar.Title>{BreadcrumbContent}</TopBar.Title>
        </TopBarContent>
        <InsufficientPermissions />
      </div>
    )
  }

  // Otherwise, we have permissions and can show the settings UI
  return (
    <div>
      <TopBarContent>
        <TopBar.Title>{BreadcrumbContent}</TopBar.Title>
        <TopBar.Tabs
          selectedKey={selectedTab}
          onSelectionChange={(value) => {
            if (value === 'billing') {
              navigate('/dashboard/pay')
            }
            else {
              setSelectedTab(value)
            }
          }}
        >
          <TopBar.Tabs.List>
            <TopBar.Tabs.Tab key="general" id="general">
              General
            </TopBar.Tabs.Tab>
            <TopBar.Tabs.Tab key="users" id="users">
              Users
            </TopBar.Tabs.Tab>
            <TopBar.Tabs.Tab key="api" id="api">
              API Keys
            </TopBar.Tabs.Tab>
            {org?.org_type === 'enterprise' && (
              <TopBar.Tabs.Tab key="sso" id="sso">
                SSO
              </TopBar.Tabs.Tab>
            )}
            <TopBar.Tabs.Tab key="orgs" id="orgs">
              Organizations
            </TopBar.Tabs.Tab>
            <TopBar.Tabs.Tab key="billing" id="billing">
              <div className="flex items-center">
                Billing
                <SquareArrowOutUpRight size={12} className="ml-1" />
              </div>
            </TopBar.Tabs.Tab>
          </TopBar.Tabs.List>
        </TopBar.Tabs>
        <TopBar.Actions />
      </TopBarContent>

      <div className="p-5">
        {selectedTab === 'general' && <SettingsPage />}
        {selectedTab === 'api' && <Keys />}
        {selectedTab === 'sso' && org?.org_type === 'enterprise' && <SSOSettings />}
        {selectedTab === 'orgs' && <OrgHome />}
        {selectedTab === 'users' && <OrgManagement />}
      </div>
    </div>
  )
}
